import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, Users, Globe2, Layers, ArrowRight, Sparkles, CheckCircle2, MessageSquare, Cpu, Shield, Headphones } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const CustomAIPage = () => {
  const useCases = [
    { icon: Building2, text: 'High-volume brands', description: 'Handling thousands of daily conversations' },
    { icon: Users, text: 'Enterprise Shopify Plus', description: 'Advanced features for enterprise stores' },
    { icon: Globe2, text: 'Marketplaces', description: 'Multi-vendor AI assistance' },
    { icon: Layers, text: 'Multi-store operations', description: 'Unified AI across all your stores' }
  ];

  const buildIncludes = [
    { icon: MessageSquare, title: 'Brand-aligned AI Personality', description: 'AI that speaks in your brand voice and follows your guidelines' },
    { icon: Cpu, title: 'Custom Integrations', description: 'Connect with your existing CRM, ERP, and business tools' },
    { icon: Shield, title: 'Advanced Security', description: 'Enterprise-grade security with custom compliance requirements' },
    { icon: Headphones, title: 'Priority Support', description: 'Dedicated success manager and 24/7 priority support' }
  ];

  return (
    <div className="min-h-screen bg-[#FDFDFD]" data-testid="custom-ai-page">
      <Navbar />
      
      <main className="pt-32 pb-24">
        {/* Hero */}
        <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 mb-20">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center gap-2 bg-slate-900 text-white rounded-full px-4 py-1.5 mb-6">
              <Sparkles className="w-4 h-4 text-lime-400" />
              <span className="text-sm font-medium">Enterprise Solutions</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
              Custom AI Chatbot{' '}
              <span className="gradient-text">Built for Your Brand</span>
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We build tailored AI-powered commerce assistants that align with your brand voice, integrate with your existing systems, and scale with your business.
            </p>
          </motion.div>
        </section>

        {/* Use Cases */}
        <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 mb-24">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Perfect For</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {useCases.map((item, index) => (
              <motion.div
                key={item.text}
                className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-lg transition-shadow"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mb-4">
                  <item.icon className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.text}</h3>
                <p className="text-slate-600 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* What's Included */}
        <section className="bg-gradient-to-b from-slate-50 to-white py-24 border-y border-slate-200">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Custom AI Build Includes</h2>
              <p className="text-slate-600 max-w-2xl mx-auto">
                Everything you need for a fully tailored AI commerce assistant
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {buildIncludes.map((item, index) => (
                <motion.div
                  key={item.title}
                  className="bg-white rounded-2xl p-8 border-2 border-slate-100 shadow-sm hover:shadow-lg hover:border-teal-200 transition-all duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="w-14 h-14 rounded-2xl bg-teal-50 border border-teal-200 flex items-center justify-center mb-6">
                    <item.icon className="w-7 h-7 text-teal-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{item.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{item.description}</p>
                </motion.div>
              ))}
            </div>

            {/* Full Feature List */}
            <motion.div 
              className="mt-16 bg-white rounded-2xl p-8 border-2 border-teal-100 shadow-sm"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-semibold text-slate-900 mb-6 text-center">Everything Included</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  'Brand-aligned AI personality',
                  'Custom integrations & workflows',
                  'Advanced analytics dashboard',
                  'Priority support & SLA',
                  'Dedicated success manager',
                  'Multi-language support',
                  'Custom training data',
                  'A/B testing framework',
                  'Advanced reporting'
                ].map((feature) => (
                  <div key={feature} className="flex items-center gap-3 text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-teal-500 flex-shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24">
          <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Ready to Build Your Custom AI?
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Let's discuss your requirements and create an AI assistant tailored to your brand.
              </p>
              <Link to="/contact">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white group">
                  Request Custom AI Build
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CustomAIPage;
